<template>
    <div class="push-trigger d-flex align-items-center" ref="triggerWrapperRef">
        <span v-if="pushState !== 'subscribed'" class="push-hint d-inline me-2">Drück die Glocke! 😉</span>
        <button type="button" class="push-bell-btn" ref="bellButtonRef" @click="togglePanel" :title="bellTitle"
            :aria-label="bellTitle" aria-haspopup="dialog" :aria-expanded="isPanelOpen">
            <font-awesome-icon :icon="bellIcon" class="push-bell-icon" :class="bellIconClass" />
            <span v-if="pushState === 'subscribed'" class="push-badge" aria-hidden="true">
                <font-awesome-icon icon="circle-check" />
            </span>
        </button>
    </div>

    <Teleport to="body">
        <Transition :name="isMobileSheet ? 'push-sheet' : 'push-panel'">
            <div v-if="isPanelOpen" ref="panelRef" class="push-panel" :class="{ 'push-panel--sheet': isMobileSheet }"
                :style="isMobileSheet ? {} : panelPositionStyle" role="dialog" aria-label="Push-Benachrichtigungen">
                <button type="button" class="push-panel-close" @click="closePanel" aria-label="Schließen">×</button>

                <h3 class="push-panel-headline">{{ panelContent.headline }}</h3>
                <p class="push-panel-body">{{ panelContent.body }}</p>

                <button v-if="panelContent.showCta" type="button" class="modern-btn push-panel-cta"
                    :disabled="pushState === 'subscribing'" @click="subscribeToNotifications">
                    {{ panelContent.ctaLabel }}
                    <span v-if="pushState === 'subscribing'" class="spinner-border spinner-border-sm ms-2"
                        role="status" aria-hidden="true"></span>
                </button>
            </div>
        </Transition>
    </Teleport>
</template>

<script>
import axios from 'axios';
import { toast } from 'vue3-toastify';
import { ref, computed, onMounted, onUnmounted, nextTick } from 'vue';

export default {
    setup() {
        const pushState = ref('idle'); // 'unsupported' | 'idle' | 'subscribing' | 'subscribed' | 'denied' | 'error'
        const justSubscribed = ref(false);
        const introShake = ref(false);
        const isPanelOpen = ref(false);
        const isMobileSheet = ref(false);
        const panelPositionStyle = ref({});

        const triggerWrapperRef = ref(null);
        const bellButtonRef = ref(null);
        const panelRef = ref(null);

        let mobileMediaQuery = null;

        const isPushSupported = () =>
            'serviceWorker' in navigator && 'PushManager' in window && 'Notification' in window;

        const bellIcon = computed(() =>
            (pushState.value === 'denied' || pushState.value === 'unsupported') ? 'bell-slash' : 'bell'
        );

        const bellIconClass = computed(() => ({
            'push-bell-icon--muted': pushState.value === 'denied' || pushState.value === 'unsupported',
            'push-bell-icon--pulse': pushState.value === 'idle' && !introShake.value,
            'fa-shake': introShake.value,
        }));

        const bellTitle = computed(() => {
            switch (pushState.value) {
                case 'subscribed': return 'Du bekommst schon Bescheid 🔔';
                case 'denied': return 'Benachrichtigungen sind blockiert';
                case 'unsupported': return 'Push wird von deinem Browser leider nicht unterstützt';
                default: return 'Benachrichtigungen aktivieren';
            }
        });

        const idleCopy = {
            headline: 'Auf dem laufenden bleiben',
            body: 'Werde bei größeren Stimmungsausschlägen benachrichtigt. Kein Spam, versprochen!',
        };

        const panelContent = computed(() => {
            switch (pushState.value) {
                case 'unsupported':
                    return {
                        headline: 'Dein Browser spielt nicht mit 😅',
                        body: 'Push-Benachrichtigungen werden von diesem Browser oder Gerät leider nicht unterstützt. Probier\'s mal mit einem aktuellen Chrome, Firefox oder Edge.',
                        showCta: false,
                    };
                case 'denied':
                    return {
                        headline: 'Das hat nicht geklappt...',
                        body: 'Du hast Benachrichtigungen blockiert. Du musst über die Browser-Einstellungen Pushbenachrichtigungen zulassen.',
                        showCta: false,
                    };
                case 'subscribed':
                    return justSubscribed.value
                        ? {
                            headline: 'Du bist dabei! 🎉',
                            body: 'Ab sofort wirst du informiert, wenn es etwas berichtenswertes zu vemelden gibt. 😉',
                            showCta: false,
                        }
                        : {
                            headline: 'Abonnement aktiviert',
                            body: 'Du hast die Benachrichtigungen bereits aktiviert. Für dich gibt\'s hier nichts weiter zu tun.',
                            showCta: false,
                        };
                case 'error':
                    return {
                        headline: 'Das hat nicht geklappt',
                        body: 'Da ist wohl etwas schiefgelaufen.',
                        showCta: true,
                        ctaLabel: 'Nochmal versuchen 🔔',
                    };
                case 'subscribing':
                    return { ...idleCopy, showCta: true, ctaLabel: 'Wird aktiviert...' };
                default:
                    return { ...idleCopy, showCta: true, ctaLabel: 'Abonnieren 🔔' };
            }
        });

        const updatePanelPosition = () => {
            if (isMobileSheet.value || !bellButtonRef.value) return;
            const rect = bellButtonRef.value.getBoundingClientRect();
            const panelWidth = 320;
            const left = Math.max(12, Math.min(rect.right - panelWidth, window.innerWidth - panelWidth - 12));
            panelPositionStyle.value = {
                position: 'fixed',
                top: `${rect.bottom + 10}px`,
                left: `${left}px`,
            };
        };

        const handleDocumentClick = (event) => {
            if (triggerWrapperRef.value?.contains(event.target) || panelRef.value?.contains(event.target)) {
                return;
            }
            closePanel();
        };

        const handleKeydown = (event) => {
            if (event.key === 'Escape') closePanel();
        };

        const openPanel = async () => {
            isPanelOpen.value = true;
            await nextTick();
            updatePanelPosition();
            document.addEventListener('click', handleDocumentClick);
            document.addEventListener('keydown', handleKeydown);
            window.addEventListener('scroll', updatePanelPosition, { passive: true });
            window.addEventListener('resize', updatePanelPosition);
        };

        const closePanel = () => {
            isPanelOpen.value = false;
            document.removeEventListener('click', handleDocumentClick);
            document.removeEventListener('keydown', handleKeydown);
            window.removeEventListener('scroll', updatePanelPosition);
            window.removeEventListener('resize', updatePanelPosition);
        };

        const togglePanel = () => {
            if (isPanelOpen.value) {
                closePanel();
            } else {
                openPanel();
            }
        };

        const handleMobileChange = (event) => {
            isMobileSheet.value = event.matches;
        };

        onMounted(async () => {
            mobileMediaQuery = window.matchMedia('(max-width: 575.98px)');
            isMobileSheet.value = mobileMediaQuery.matches;
            mobileMediaQuery.addEventListener('change', handleMobileChange);

            introShake.value = true;
            setTimeout(() => { introShake.value = false; }, 1800);

            if (!isPushSupported()) {
                pushState.value = 'unsupported';
                return;
            }
            if (Notification.permission === 'denied') {
                pushState.value = 'denied';
                return;
            }
            try {
                const registration = await navigator.serviceWorker.ready;
                const existing = await registration.pushManager.getSubscription();
                pushState.value = existing ? 'subscribed' : 'idle';
            } catch (error) {
                console.error('Konnte bestehende Push-Subscription nicht ermitteln:', error);
                pushState.value = 'idle';
            }
        });

        onUnmounted(() => {
            mobileMediaQuery?.removeEventListener('change', handleMobileChange);
            document.removeEventListener('click', handleDocumentClick);
            document.removeEventListener('keydown', handleKeydown);
            window.removeEventListener('scroll', updatePanelPosition);
            window.removeEventListener('resize', updatePanelPosition);
        });

        const subscribeToNotifications = async () => {
            if (!isPushSupported()) {
                pushState.value = 'unsupported';
                return;
            }

            pushState.value = 'subscribing';

            try {
                const permission = await Notification.requestPermission();

                if (permission === 'denied') {
                    pushState.value = 'denied';
                    return;
                }

                if (permission !== 'granted') {
                    pushState.value = 'idle';
                    toast.info('Kein Ding – du kannst die Glocke jederzeit nochmal drücken.');
                    return;
                }

                const registration = await navigator.serviceWorker.ready;
                const vapidKey = process.env.VUE_APP_PUBLIC_VAPID_KEY;

                const subscription = await registration.pushManager.subscribe({
                    userVisibleOnly: true,
                    applicationServerKey: urlBase64ToUint8Array(vapidKey),
                });

                await sendSubscriptionToServer(subscription);
                justSubscribed.value = true;
                pushState.value = 'subscribed';
            } catch (error) {
                console.error('Failed to subscribe the user: ', error);
                pushState.value = 'error';
                toast.error('Fehler bei der Registrierung des Geräts für Pushnachrichten');
            }
        };

        const sendSubscriptionToServer = async (subscription) => {
            const enhancedSubscription = {
                ...subscription.toJSON(),
                subdomain: 'stimmung',
                username: localStorage.getItem('selectedSlug'),
            };
            const response = await axios.post(`${process.env.VUE_APP_BACKEND_URL}/pushnotifications/subscribe`, enhancedSubscription, {
                headers: { 'Content-Type': 'application/json' },
            });

            if (response.status !== 200) throw new Error('Failed to send subscription to server');

            toast.success('Dein Gerät wurde erfolgreich für den Empfang von Pushnachrichten registriert! 🔔');
        };

        const urlBase64ToUint8Array = (base64String) => {
            const padding = '='.repeat((4 - (base64String.length % 4)) % 4);
            const base64 = (base64String + padding).replace(/-/g, '+').replace(/_/g, '/');
            const rawData = window.atob(base64);
            return Uint8Array.from([...rawData].map((char) => char.charCodeAt(0)));
        };

        return {
            pushState,
            isPanelOpen,
            isMobileSheet,
            panelPositionStyle,
            triggerWrapperRef,
            bellButtonRef,
            panelRef,
            bellIcon,
            bellIconClass,
            bellTitle,
            panelContent,
            togglePanel,
            closePanel,
            subscribeToNotifications,
        };
    },
};
</script>

<style scoped>
.push-trigger {
    position: relative;
}

.push-hint {
    color: var(--text-secondary);
    font-size: var(--fs-sm);
    font-weight: 500;
    white-space: nowrap;
}

.push-bell-btn {
    position: relative;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 2.5rem;
    height: 2.5rem;
    background: var(--secondary-bg);
    border: 1px solid var(--card-border);
    border-radius: 50%;
    color: var(--text-primary);
    cursor: pointer;
    transition: background-color 0.2s ease, transform 0.2s ease;
}

.push-bell-btn:hover {
    background: var(--card-bg);
    transform: translateY(-1px);
}

.push-bell-icon {
    font-size: var(--fs-lg);
}

.push-bell-icon--muted {
    opacity: 0.6;
}

.push-bell-icon--pulse {
    animation: push-bell-pulse 7s ease-in-out infinite;
}

@keyframes push-bell-pulse {

    0%,
    85%,
    100% {
        transform: scale(1);
    }

    90% {
        transform: scale(1.12);
    }
}

.push-badge {
    position: absolute;
    top: -2px;
    right: -2px;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    width: 1rem;
    height: 1rem;
    background: var(--overlay-strong);
    color: var(--success);
    border-radius: 50%;
    font-size: 0.65rem;
}

.push-panel {
    position: fixed;
    width: min(320px, calc(100vw - 24px));
    background: var(--card-bg);
    backdrop-filter: blur(10px);
    border: 1px solid var(--card-border);
    border-radius: var(--radius-lg);
    box-shadow: var(--shadow-xl);
    padding: var(--spacing-lg);
    font-family: var(--font-family);
    color: var(--text-primary);
    z-index: 1050;
}

.push-panel--sheet {
    left: 0;
    right: 0;
    bottom: 0;
    top: auto;
    width: 100%;
    border-radius: var(--radius-lg) var(--radius-lg) 0 0;
    padding-bottom: calc(var(--spacing-xl) + env(safe-area-inset-bottom, 0px));
}

.push-panel-close {
    position: absolute;
    top: var(--spacing-sm);
    right: var(--spacing-sm);
    width: 1.75rem;
    height: 1.75rem;
    display: inline-flex;
    align-items: center;
    justify-content: center;
    background: transparent;
    border: none;
    border-radius: 50%;
    color: var(--text-secondary);
    font-size: 1.25rem;
    line-height: 1;
    cursor: pointer;
}

.push-panel-close:hover {
    background: var(--secondary-bg);
    color: var(--text-primary);
}

.push-panel-headline {
    font-size: var(--fs-lg);
    font-weight: 600;
    margin: 0 var(--spacing-lg) var(--spacing-sm) 0;
}

.push-panel-body {
    font-size: var(--fs-sm);
    color: var(--text-secondary);
    margin: 0 0 var(--spacing-md) 0;
    line-height: var(--line-height-base);
}

.push-panel-cta {
    width: 100%;
    justify-content: center;
}

.push-panel-enter-active,
.push-panel-leave-active {
    transition: opacity 0.2s ease, transform 0.2s ease;
}

.push-panel-enter-from,
.push-panel-leave-to {
    opacity: 0;
    transform: translateY(-8px);
}

.push-sheet-enter-active,
.push-sheet-leave-active {
    transition: transform 0.25s ease;
}

.push-sheet-enter-from,
.push-sheet-leave-to {
    transform: translateY(100%);
}
</style>
